import React, { useCallback, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import type { MessageActions } from './use-message-actions';

export type ContextMenuAnchor =
    | { anchorEl: HTMLElement; anchorPosition?: undefined }
    | { anchorEl?: undefined; anchorPosition: { top: number; left: number } };

interface MessageContextMenuProps {
    message: any;
    actions: MessageActions;
    open: boolean;
    /** Element anchor (chevron) or cursor position (right-click). */
    anchor: ContextMenuAnchor | null;
    onClose: () => void;
    /** Opens the quick-reaction bar (owned by the hover action bar). */
    onOpenReactionBar: () => void;
}

const menuItemSx = {
    fontSize: '13px',
    minHeight: '34px',
    color: 'var(--wa-title-text, #111b21)',
    '&:hover': { backgroundColor: 'var(--wa-row-hover-bg, rgba(134,150,160,0.1))' },
    '& .MuiListItemIcon-root': {
        minWidth: '30px',
        color: 'var(--wa-hover-action-icon, rgba(0,0,0,0.6))',
        '& svg': { fontSize: 18 },
    },
    '& .MuiListItemText-primary': { fontSize: '13px' },
} as const;

/**
 * WhatsApp-parity message context menu (chevron / right-click): Reply, Copy,
 * React, Forward, Pin, Star, Delete — with a small confirm dialog for Delete
 * ('for me' always, 'for everyone' when fromMe). All behavior comes from
 * useMessageActions so the hover bar and this menu share one implementation.
 */
const MessageContextMenu: React.FC<MessageContextMenuProps> = ({
    message,
    actions,
    open,
    anchor,
    onClose,
    onOpenReactionBar,
}) => {
    const { t } = useTranslation();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const isStarred = !!message?.star;
    const fromMe = !!message?.fromMe;

    const run = useCallback((fn: () => void) => () => {
        onClose();
        fn();
    }, [onClose]);

    const handleDeleteFor = useCallback((forEveryone: boolean) => {
        setDeleteDialogOpen(false);
        actions.deleteMessage(forEveryone);
    }, [actions]);

    return (
        <>
            <Menu
                open={open}
                onClose={onClose}
                // MUI restores focus to the menu anchor on close, which yanks
                // focus AWAY from the composer right after Reply stages it (the
                // input-bar effect focuses the input, then the closing menu
                // steals it back). WA keeps focus in the message box.
                disableRestoreFocus
                {...(anchor?.anchorEl
                    ? { anchorEl: anchor.anchorEl, anchorReference: 'anchorEl' as const }
                    : anchor?.anchorPosition
                        ? { anchorReference: 'anchorPosition' as const, anchorPosition: anchor.anchorPosition }
                        : {})}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 180,
                            borderRadius: '8px',
                            backgroundColor: 'var(--wa-hover-action-bg, #fff)',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
                            py: 0,
                        },
                    },
                }}
            >
                <MenuItem sx={menuItemSx} onClick={run(actions.replyToMessage)}>
                    <ListItemIcon><ReplyIcon /></ListItemIcon>
                    <ListItemText>{t('teamInbox.menuReply', { defaultValue: 'Reply' })}</ListItemText>
                </MenuItem>

                {actions.canCopy && (
                    <MenuItem sx={menuItemSx} onClick={run(actions.copyText)}>
                        <ListItemIcon><ContentCopyIcon /></ListItemIcon>
                        <ListItemText>{t('teamInbox.menuCopy', { defaultValue: 'Copy' })}</ListItemText>
                    </MenuItem>
                )}

                <MenuItem sx={menuItemSx} disabled={!actions.canReact} onClick={run(onOpenReactionBar)}>
                    <ListItemIcon><AddReactionOutlinedIcon /></ListItemIcon>
                    <ListItemText>{t('teamInbox.react', { defaultValue: 'React' })}</ListItemText>
                </MenuItem>

                <MenuItem sx={menuItemSx} disabled={!actions.canForward} onClick={run(actions.forwardMessage)}>
                    <ListItemIcon><ShortcutIcon /></ListItemIcon>
                    <ListItemText>{t('teamInbox.forward', { defaultValue: 'Forward' })}</ListItemText>
                </MenuItem>

                {/* Pin only (no per-message pinned state in the slim contract yet —
                    tracking it would need waGetPinnedMessagesTool bookkeeping). */}
                <MenuItem sx={menuItemSx} disabled={!actions.canPin} onClick={run(() => { actions.pinMessage(); })}>
                    <ListItemIcon><PushPinOutlinedIcon /></ListItemIcon>
                    <ListItemText>{t('teamInbox.menuPin', { defaultValue: 'Pin' })}</ListItemText>
                </MenuItem>

                <MenuItem
                    sx={menuItemSx}
                    disabled={!actions.canStar}
                    onClick={run(() => { actions.starMessage(!isStarred); })}
                >
                    <ListItemIcon>{isStarred ? <StarIcon /> : <StarBorderIcon />}</ListItemIcon>
                    <ListItemText>
                        {isStarred
                            ? t('teamInbox.menuUnstar', { defaultValue: 'Unstar' })
                            : t('teamInbox.menuStar', { defaultValue: 'Star' })}
                    </ListItemText>
                </MenuItem>

                <Divider sx={{ my: '4px !important' }} />

                <MenuItem
                    sx={{ ...menuItemSx, color: 'var(--danger, #ea0038)', '& .MuiListItemIcon-root': { ...menuItemSx['& .MuiListItemIcon-root'], color: 'var(--danger, #ea0038)' } }}
                    disabled={!actions.canDelete}
                    onClick={() => { onClose(); setDeleteDialogOpen(true); }}
                >
                    <ListItemIcon><DeleteOutlineIcon /></ListItemIcon>
                    <ListItemText>{t('teamInbox.menuDelete', { defaultValue: 'Delete' })}</ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete confirm dialog (WA: 'Delete for me' always; 'for everyone' for own msgs) */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '12px', minWidth: 320 } }}
            >
                <DialogTitle sx={{ fontSize: '16px' }}>
                    {t('teamInbox.deleteMessageTitle', { defaultValue: 'Delete message?' })}
                </DialogTitle>
                <DialogActions sx={{ flexDirection: 'column', alignItems: 'flex-end', gap: '4px', pb: 2, pr: 2 }}>
                    {fromMe && (
                        <Button color="error" onClick={() => handleDeleteFor(true)}>
                            {t('teamInbox.deleteForEveryone', { defaultValue: 'Delete for everyone' })}
                        </Button>
                    )}
                    <Button color="error" onClick={() => handleDeleteFor(false)}>
                        {t('teamInbox.deleteForMe', { defaultValue: 'Delete for me' })}
                    </Button>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default React.memo(MessageContextMenu);
